import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import UserImg from "@styles/all-images/images/Avatar.png";
import UnreadMsgIcon from "@styles/all-images/icons/unreadmsg.svg";
import UnreadMsgRightIcon from "@styles/all-images/icons/unreadmsgright.svg";
import MsgSendIcon from "@styles/all-images/icons/msgsend.svg";
import {
  ChatMessageListenerSource,
  ChatScreenType,
  ChatUser,
  Message,
} from "../types/chat";
import { GroupInfoResponse } from "../types/api-response-types";
import { useLiveChatState } from "../Provider/LiveChatStateProvider";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import ChatMessagesSkeleton from "./skeletons/ChatMessagesSkeleton";
import { RootState } from "@/store";
import webSocketHandler, {
  SocketMessage,
  WebSocketParams,
} from "@/composables/webSocketHandler";
import { toast } from "react-toastify";
import dateUtil from "@/composables/dateUtil";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import debounceHandler from "@/composables/debounceHandler";
import { ReceivedMessageType } from "../types/chat.enums";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";
import { S3Image } from "@/utils/s3-helper";

interface ChatMessagesProps {
  onReplyClick?: (message: Message) => void;
  isThread?: boolean;
  isGroup?: boolean;
  isDocument?: boolean;
  isStandalone?: boolean;
  groupInfo?: GroupInfoResponse;
  onAddMember?: () => void;
  threadMessageId?: number;
  documentId?: number;
  standaloneUserInfo?: ChatUser;
  customRoomId?: string;
}

interface APIMessage {
  message_id: number;
  sender: {
    id: number;
    sender_name: string;
  };
  message: string;
  created_at: string;
  chat_room_id: number;
  document_id: number | null;
  replies: APIMessage[];
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  onReplyClick,
  isThread = false,
  isGroup = false,
  isStandalone = false,
  isDocument = false,
  groupInfo,
  onAddMember,
  threadMessageId,
  documentId,
  standaloneUserInfo,
  customRoomId,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { currentScreen, userInfo, companyInfo } = useLiveChatState();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const [threadParentMessage, setThreadParentMessage] =
    useState<Message | null>(null);
  const [message, setMessage] = useState("");
  const { handleListening, handleSending, handleClosing } = webSocketHandler();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toDesiredFormat } = dateUtil();
  const [isSending, setIsSending] = useState(false);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [forceReset, setForceReset] = useState(false);
  const debouncedScrollRef = useRef<any>(null);
  const { debouncedCallback } = debounceHandler();
  const [retrySendingCount, setRetrySendingCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const maxRetrySendingCount = 3;
  const showWelcomeMessage = useMemo(() => {
    return isGroup && messages.length === 0 && groupInfo;
  }, [isGroup, messages, groupInfo]);

  const isChatTerminated = useMemo(() => {
    return isGroup && groupInfo?.is_terminated;
  }, [isGroup, groupInfo]);

  const heightMapping = useMemo(() => {
    let returnValue = "calc(100vh - 375px)";
    if (isGroup) {
      returnValue = "calc(100vh - 350px)";
    } else if (isThread && isStandalone) {
      returnValue = "100%";
    } else if (isThread) {
      returnValue = "calc(100vh - 315px)";
    } else if (isDocument) {
      returnValue = "calc(100vh - 480px)";
    } else if (isStandalone) {
      returnValue = "100%";
    } else if (companyInfo?.user_type_id !== UserTierType.Tier4) {
      returnValue = "calc(100vh - 339px)";
    }
    return returnValue;
  }, [isGroup, isThread, isDocument]);

  const transformAPIMessage = (apiMessage: APIMessage): Message => {
    return {
      id: apiMessage.message_id,
      user: {
        name: apiMessage.sender.sender_name,
        id: apiMessage.sender.id,
      },
      content: apiMessage.message,
      metadata: {
        timestamp: apiMessage.created_at,
        reads: 0, // Add if available in API
        replies: apiMessage.replies?.length || 0,
      },
      isCreated: apiMessage.sender.id === user?.id,
    };
  };

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const getAPIParams = useCallback(
    (isRealTime: boolean): Promise<{ params: string[]; queryParams: any }> => {
      return new Promise(async (resolve, reject) => {
        let params: string[] = [];
        let queryParams: any = {};

        await sleep(100);

        if (isThread && threadMessageId) {
          params = [threadMessageId.toString() + "/"];
        }

        if (currentScreen?.type === ChatScreenType.GROUP && !isStandalone) {
          queryParams = {
            chat_room_id: customRoomId || currentScreen.data.group?.room_id,
          };
        } else {
          queryParams = {
            chat_room_id:
              customRoomId ||
              (isStandalone ? standaloneUserInfo?.room_id : userInfo?.room_id),
            document_id: documentId,
          };
          if (!documentId) delete queryParams.document_id;
        }

        queryParams.page = !isRealTime ? page : 1;
        queryParams.limit = limit;

        resolve({ params, queryParams });
      });
    },
    [
      page,
      isThread,
      threadMessageId,
      currentScreen,
      userInfo,
      standaloneUserInfo,
      documentId,
      limit,
      customRoomId,
    ],
  );

  interface FetchMessagesParams {
    isRealTime?: boolean;
    disableLoading?: boolean;
  }

  const fetchMessages = useCallback(
    ({ isRealTime = false, disableLoading = false }: FetchMessagesParams) => {
      return new Promise(async (resolve, reject) => {
        try {
          console.log("hasMore: ", hasMore);
          if (!hasMore && !isRealTime) {
            return;
          }
          if (!disableLoading) {
            setIsLoading(true);
          }
          setHasMore(true);
          const { authenticatedCall } = actionsHandler("getMessages");

          const { params, queryParams } = await getAPIParams(isRealTime);
          console.log("queryParams: ", queryParams);
          if (!queryParams.chat_room_id) {
            setIsLoading(false);
            return;
          }

          const query = new URLSearchParams(queryParams);

          const response = await dispatch(
            authenticatedCall({
              method: "get",
              customQuery: query.toString(),
              customParams: params,
            }),
          );

          if (response.ok) {
            handleSavingMessages(response, queryParams, isRealTime);
            setIsInitialLoading(false);
            resolve(true);
          }
        } catch (error) {
          console.error("Error fetching messages:", error);
          reject(error);
        } finally {
          setIsLoading(false);
        }
      });
    },
    [
      page,
      isThread,
      threadMessageId,
      currentScreen,
      userInfo,
      standaloneUserInfo,
      documentId,
      limit,
      hasMore,
      getAPIParams,
    ],
  );

  const handleSavingMessages = async (
    response: any,
    queryParams: any,
    isRealTime: boolean,
  ) => {
    let messagesData = response.data?.data;
    if (isThread) {
      const threadResponse = response.data?.data || {};
      messagesData = threadResponse?.replies;
      setThreadParentMessage({
        id: threadResponse.message_id,
        user: {
          id: threadResponse.sender?.id,
          name: threadResponse.sender?.sender_name,
        },
        content: threadResponse.message,
        isCreated: threadResponse.sender?.id === user?.id,
        metadata: {
          timestamp: threadResponse.created_at,
          reads: 0,
          replies: 0,
        },
      });
    }
    const transformedMessages = messagesData?.map?.(transformAPIMessage) || [];

    // TODO: Reverse the spread after correcting the pagination
    let newMessages =
      page === 1 || isThread || isRealTime
        ? transformedMessages
        : [...transformedMessages, ...messages];
    newMessages = newMessages.filter(
      (message: Message, index: number, self: Message[]) =>
        index === self.findIndex((t: Message) => t.id === message.id),
    );

    const newPage = !isRealTime ? queryParams.page + 1 : 2;
    setPage(newPage);
    setMessages(newMessages);
    setHasMore(!isThread && !!response.data.meta.next);

    if (isRealTime) {
      await sleep(300);
      scrollToBottom();
    }
  };

  const scrollToBottom = async () => {
    await sleep(500);
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    messageInputRef.current?.focus();
  };

  const getRoomId = () => {
    if (currentScreen?.type === ChatScreenType.GROUP && !isStandalone) {
      return customRoomId || groupInfo?.id.toString() || "";
    } else {
      return (
        customRoomId || userInfo?.room_id || standaloneUserInfo?.room_id || ""
      );
    }
  };

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (e.currentTarget.scrollTop <= 10) {
        e.currentTarget.scrollTop = 10;
      }
      // Original pagination logic
      debouncedCallback({
        debounceRef: debouncedScrollRef,
        callback: async () => {
          if (!containerRef.current || isLoading || isLoadingMore || !hasMore)
            return;
          const { scrollTop } = containerRef.current;

          if (scrollTop <= 100) {
            setIsLoadingMore(true);
            await fetchMessages({
              disableLoading: true,
            });
            setIsLoadingMore(false);
          }
        },
      });
    },
    [isLoading, hasMore, page, fetchMessages],
  );

  const handleSendMessage = useCallback(async () => {
    if (!message.trim()) return;

    try {
      const params = {
        roomId: getRoomId(),
        threadId: threadMessageId?.toString(),
        documentId: documentId?.toString(),
      };

      setIsSending(true);
      await handleSending(params, message);
      setMessage("");
      setIsRetrying(false);
      setRetrySendingCount(0);
      fetchMessages({
        isRealTime: true,
        disableLoading: true,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
      // if (!isRetrying && retrySendingCount < maxRetrySendingCount) {
      //   setIsRetrying(true);
      //   setTimeout(() => {
      //     toast.info(
      //       `Retrying to send message, try ${retrySendingCount + 1} of ${maxRetrySendingCount}`,
      //     );
      //     handleSendMessage();
      //   }, 1000);
      // }
    } finally {
      setIsSending(false);
    }
  }, [
    message,
    retrySendingCount,
    getRoomId,
    threadMessageId,
    documentId,
    isRetrying,
    handleSending,
    fetchMessages,
  ]);

  useEffect(() => {
    if (!isInitialLoading) {
      setPage(1);
      setHasMore(true);
      setForceReset(true);
    }
  }, [
    currentScreen,
    threadMessageId,
    documentId,
    isThread,
    standaloneUserInfo,
    customRoomId,
  ]);

  useEffect(() => {
    if (forceReset) {
      fetchMessages({});
      setForceReset(false);
    }
  }, [forceReset]);

  useEffect(() => {
    fetchMessages({});
  }, []);

  const getReceivedMessageType = (
    data: SocketMessage,
    params: WebSocketParams,
  ): ReceivedMessageType => {
    if (Number(data.chat_id) !== Number(params.roomId)) {
      return ReceivedMessageType.notRelevant;
    }

    if (data.parent_id === "None") {
      if (Number(data.sender) === Number(user?.id)) {
        return ReceivedMessageType.directMessageSent;
      } else {
        return ReceivedMessageType.directMessageReceived;
      }
    } else if (data.parent_id !== "None") {
      if (params.threadId && params.threadId === data.parent_id) {
        if (Number(data.sender) === Number(user?.id)) {
          return ReceivedMessageType.threadMessageSent;
        } else {
          return ReceivedMessageType.threadMessageReceived;
        }
      } else {
        if (Number(data.sender) === Number(user?.id)) {
          return ReceivedMessageType.threadMessageParentSent;
        } else {
          return ReceivedMessageType.threadMessageParentReceived;
        }
      }
    } else {
      return ReceivedMessageType.notRelevant;
    }
  };

  const handleChangingReplyCount = useCallback(
    (data: SocketMessage, messageType: ReceivedMessageType) => {
      const message = messages.find(
        (message) => message.id === Number(data.parent_id),
      );
      if (message) {
        message.metadata.replies = (message.metadata?.replies || 0) + 1;
        if (messageType === ReceivedMessageType.threadMessageParentReceived) {
          message.metadata.reads = (message.metadata?.reads || 0) + 1;
        }
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === message.id
              ? { ...msg, metadata: message.metadata }
              : msg,
          ),
        );
      }
    },
    [messages],
  );

  useEffect(() => {
    try {
      if (!currentScreen && !isStandalone) return;

      const params = {
        roomId: getRoomId(),
        threadId: threadMessageId?.toString(),
        documentId: documentId?.toString(),
        from: ChatMessageListenerSource.CHAT_SCREEN,
      };

      let close: (() => void) | undefined;

      handleListening(params, async (data) => {
        const messageType = getReceivedMessageType(data, params);
        if (
          messageType === ReceivedMessageType.directMessageSent ||
          messageType === ReceivedMessageType.directMessageReceived ||
          messageType === ReceivedMessageType.threadMessageSent ||
          messageType === ReceivedMessageType.threadMessageReceived
        ) {
          await fetchMessages({
            isRealTime: true,
            disableLoading: true,
          });
          scrollToBottom();
        } else if (
          messageType === ReceivedMessageType.threadMessageParentSent ||
          messageType === ReceivedMessageType.threadMessageParentReceived
        ) {
          handleChangingReplyCount(data, messageType);
        }
      }).then((res) => {
        close = res?.close;
      });

      return () => {
        close?.();
      };
    } catch (error) {
      console.error("Error listening to messages:", error);
    }
  }, [
    currentScreen,
    threadMessageId,
    documentId,
    standaloneUserInfo,
    customRoomId,
    handleChangingReplyCount,
  ]);

  useEffect(() => {
    if (page - 1 === 1) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (messageInputRef.current && !isLoading) {
      messageInputRef.current.focus();
    }
  }, [isLoading, messages]);

  const getMessageDateDisplay = (timestamp: string) => {
    if (!timestamp) return "";

    const messageDate = new Date(timestamp);
    const today = new Date();

    // Check if the message is from today
    if (messageDate.toDateString() === today.toDateString()) {
      return "Today";
    }

    // Format the date as shown in the image
    return toDesiredFormat(timestamp, "DD-MM-YYYY HH:mm:ss", "MMM DD, YYYY");
  };

  // Helper function to group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { [key: string]: Message[] } = {};

    messages.forEach((message) => {
      const date = getMessageDateDisplay(message.metadata?.timestamp);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return groups;
  };

  const renderMessageItem = (message: Message) => {
    return (
      <div
        key={message.id}
        className={`tw-space-y-2 tw-py-3 ${message.isCreated ? "tw-flex tw-items-end tw-flex-col" : ""}`}
        data-timestamp={message.metadata?.timestamp}
      >
        <div className="tw-flex tw-items-center tw-space-x-2">
          {!message.isCreated && (
            <>
              {userInfo?.profile_picture ? (
                <S3Image
                  src={
                    userInfo?.profile_picture?.replace(
                      "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                      "",
                    ) || null
                  }
                  alt="Profile"
                  className="tw-w-10 tw-h-10 tw-rounded-full"
                />
              ) : (
                <img
                  src={UserDropdown}
                  alt="Profile"
                  className="tw-w-10 tw-h-10 tw-rounded-full"
                />
              )}
              <span className="tw-font-medium tw-text-[#232323]">
                {message.user?.name || ""}
              </span>
            </>
          )}
          <span className="tw-text-sm tw-text-[#475569] tw-list-disc tw-flex tw-items-center tw-gap-2">
            {!message.isCreated && (
              <span className="tw-h-[2px] tw-w-[2px] tw-min-w-[2px] tw-min-h-[2px] tw-max-w-[2px] tw-max-h-[2px] tw-p-[3px] tw-rounded-full tw-bg-[#475569] tw-border-2 tw-border-[#FFFFFF]"></span>
            )}
            {toDesiredFormat(
              message.metadata?.timestamp || "",
              "DD-MM-YYYY HH:mm:ss",
              "MMM DD, YYYY hh:mm A",
            )}
          </span>
        </div>

        <div
          className={`tw-ml-10 tw-p-3 ${message.isCreated ? "tw-bg-[#E8F1FF]" : "tw-bg-[#F3F2F3]"} tw-rounded-lg ${isThread ? "tw-max-w-[374px]" : "tw-max-w-[558px]"}`}
        >
          <p className="tw-text-[#1E293B] tw-text-sm tw-font-normal">
            {message.content}
          </p>
        </div>

        {!isThread && onReplyClick && (
          <button
            onClick={() => onReplyClick(message)}
            className={`tw-ml-10 tw-flex tw-items-center ${message.isCreated ? "tw-flex-row-reverse" : ""}`}
          >
            <img
              src={message.isCreated ? UnreadMsgRightIcon : UnreadMsgIcon}
              alt="Reply"
              className="tw-w-5 tw-h-5"
            />
            <span
              className={`tw-text-[#146C94] tw-text-xs tw-font-medium ${message.isCreated ? "tw-mr-3" : "tw-ml-3"}`}
            >
              {message.metadata?.reads || 0} unread
            </span>
            <span
              className={`tw-text-[#475569] tw-text-xs ${message.isCreated ? "tw-mr-3" : "tw-ml-3"}`}
            >
              {message.metadata?.replies || 0} replies
            </span>
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <ChatMessagesSkeleton heightMapping={heightMapping} />;
  }

  return (
    <>
      <div
        className={`tw-bg-[#FFFFFF] tw-px-8 tw-space-y-4 tw-overflow-auto`}
        style={{
          height: heightMapping,
        }}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {showWelcomeMessage && !isChatTerminated ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
            <div className="tw-bg-[#F8FAFC] tw-rounded-xl tw-px-5 tw-py-4 tw-text-center tw-max-w-[337px]">
              <span className="tw-text-[15px] tw-font-semibold tw-text-[#232323]">
                {`${user?.name}, welcome to your new collaboration group! Let's get started:`}
              </span>
              <button
                onClick={onAddMember}
                className="tw-mx-auto tw-mt-4 tw-flex tw-text-base tw-font-medium tw-text-[#146C94]
                tw-items-center tw-border tw-border-[#146C94] tw-py-[10px] tw-px-[10px] tw-rounded-lg tw-gap-2"
              >
                Add Member
              </button>
            </div>
          </div>
        ) : (
          <>
            {isLoadingMore && (
              <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
                <CustomLoader loading={true} isWrapper={true} size="20" />
              </div>
            )}

            {isThread && (
              <>
                {threadParentMessage && renderMessageItem(threadParentMessage)}
                <div className="tw-flex tw-items-center tw-gap-2 tw-py-2">
                  <span className="tw-text-[#475569] tw-text-xs tw-font-normal tw-text-nowrap">
                    {messages.length} replies
                  </span>
                  <span className="tw-bg-[#CBD5E1] tw-h-[2px] tw-w-full tw-p-[1px]"></span>
                </div>
              </>
            )}
            {Object.entries(groupMessagesByDate(messages)).map(
              ([date, dateMessages]) => (
                <React.Fragment key={date}>
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-4">
                    <div className="tw-h-[1px] tw-flex-1 tw-bg-[#CBD5E1]"></div>
                    <span className="tw-text-[#475569] tw-text-xs tw-font-normal">
                      {date}
                    </span>
                    <div className="tw-h-[1px] tw-flex-1 tw-bg-[#CBD5E1]"></div>
                  </div>
                  {/* <div className="tw-flex tw-items-center tw-gap-2 tw-py-2">
                    <span
                      className={`tw-fixed tw-top-[320px] tw-right-[35%] tw-transform -tw-translate-x-1/2 tw-z-10 tw-transition-opacity tw-duration-300 tw-text-[#475569] tw-text-xs tw-font-normal tw-text-nowrap tw-rounded-full tw-px-2 tw-py-1 tw-bg-[#F3F2F3] tw-border tw-border-[#CBD5E1]`}
                    >
                      {date}
                    </span>
                  </div> */}
                  {dateMessages.map((message) => renderMessageItem(message))}
                </React.Fragment>
              ),
            )}

            {messages.length === 0 && !isLoading && (
              <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
                <span className="tw-text-[#475569] tw-text-sm tw-font-normal">
                  No messages yet
                </span>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="tw-px-8 tw-py-4">
        {!isChatTerminated ? (
          <div className="tw-relative tw-flex tw-items-center">
            <input
              ref={messageInputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              placeholder="Write your message here..."
              className="tw-w-full tw-pl-4 tw-pr-12 tw-py-2 tw-border tw-rounded-lg tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
            />
            <button
              onClick={!isSending ? handleSendMessage : () => {}}
              className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2"
            >
              {!isSending ? (
                <img
                  src={MsgSendIcon}
                  alt="Send"
                  className="tw-w-8 tw-h-8 hover:tw-opacity-80"
                />
              ) : (
                <CustomLoader loading={true} isWrapper={true} size="20" />
              )}
            </button>
          </div>
        ) : (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-bg-[#E9EAEB] tw-rounded-lg tw-p-4">
            <span className="tw-text-[#809199] tw-text-xl tw-font-normal">
              The chat has been terminated by the admin.
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatMessages;
